import {
  ERROR_CODE,
  NativeApplicationPacketSchema,
  type NativeApplicationPayload,
  type NativeApplicationPushNotificationData,
  PUSH_NOTIFICATION_RECEIVER_TYPE,
  RESIDENT_STATE,
  serialize,
  t,
} from "@packages/core";
import { useQuery } from "@tanstack/react-query";
import * as v from "valibot";
import { create } from "zustand";
import { QueryKey } from "./query";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

let counter = 0;
const resolves = new Map<number, { (payload: NativeApplicationPayload): void }>();

function sendPacket<T extends true | false>(
  payload: NativeApplicationPayload<false>,
  callback = false as T,
): T extends true ? Promise<NativeApplicationPayload> : void {
  const request = callback ? counter++ : null;
  const message = JSON.stringify(
    serialize(v.parse(NativeApplicationPacketSchema(), { payload, request })),
  );
  console.debug("ネイティブアプリケーションにメッセージを送信します。", message);

  let promise;
  if (request !== null) {
    promise = new Promise<NativeApplicationPayload>(function (resolve) {
      resolves.set(request, resolve);
    });
  }

  window.Android?.receiveMessage(message);
  window.webkit?.messageHandlers?.iOS?.postMessage(message);

  return promise as any;
}

function receiveMessageFromNativeApplication(message: string) {
  console.debug("ネイティブアプリケーションからメッセージを受信しました。", message);

  let packet;
  try {
    packet = v.parse(NativeApplicationPacketSchema(), JSON.parse(message));
  } catch (error) {
    console.warn("ネイティブアプリケーションパケットとしてパース出来ません。", message, error);
    return;
  }

  if (typeof packet.response === "number") {
    const resolve = resolves.get(packet.response);

    if (!resolve) {
      console.warn(
        "ネイティブアプリケーションから宛先が" +
          packet.response +
          "であるパケットを受信しましたが、宛先が見つかりません。",
        packet,
      );
      return;
    }

    resolves.delete(packet.response);
    resolve(packet.payload);
  } else {
    switch (packet.payload.type) {
      case "Error":
        console.error(
          "エラー(" +
            (packet.payload.code ?? ERROR_CODE.NATIVE_APPLICATION_ERROR) +
            ")を受信しました。" +
            packet.payload.code,
        );
        break;
      case "NewPushNotificationToken":
        console.debug("新しいプッシュ通知トークン" + packet.payload.token + "を受信しました。");
        updatePushNotificationTokenCache(packet.payload.token);
        break;
      case "PushNotification":
        console.debug("プッシュ通知を受信しました。", packet.payload.data);
        if (
          packet.payload.data.type === RESIDENT_STATE.GOT_UP ||
          packet.payload.data.type === RESIDENT_STATE.WANDERING
        ) {
          useExternalNavigationRequest.setState({ data: packet.payload.data });
        }
        break;
      default:
        console.warn(
          "ネイティブアプリケーションパケットの処理が定義されていません。",
          packet.payload,
        );
        break;
    }
  }
}

export async function requestPushNotificationPermission() {
  // TODO: 新しいネイティブアプリケーションが十分に普及したら、このブロックを削除する。
  // 互換性を保つために最初に古い方法で取得を試みる。
  {
    const response = await sendPacket({ type: "RequestPushNotificationPermissionRequest" }, true);
    switch (response.type) {
      case "RequestPushNotificationPermissionResponse":
        return response.granted;
      case "Error":
        if (response.code === "NOT_IMPLEMENTED") {
          console.debug(
            "新しいネイティブアプリケーションであるため、新しい方法でプッシュ通知の許可を再取得します。",
          );
        } else {
          throw new Error(
            "`RequestPushNotificationPermissionResponse`以外のレスポンスが返されました。" +
              JSON.stringify(response),
          );
        }
        break;
      default:
        throw new Error(
          "`RequestPushNotificationPermissionResponse`以外のレスポンスが返されました。" +
            JSON.stringify(response),
        );
    }
  }

  const response = await sendPacket({ type: "PushNotificationPermissionRequest" }, true);
  switch (response.type) {
    case "PushNotificationPermissionResponse":
      return response.granted;
    case "Error":
      if (response.code === "NOT_IMPLEMENTED") {
        throw new Error(t({ ja: "プッシュ通知の許可に対応していません。" }));
      } else {
        throw new Error(
          "`PushNotificationPermissionResponse`以外のレスポンスが返されました。" +
            JSON.stringify(response),
        );
      }
    default:
      throw new Error(
        "`PushNotificationPermissionResponse`以外のレスポンスが返されました。" +
          JSON.stringify(response),
      );
  }
}

export async function setNativeApplicationTextToSpeechPitch(pitch: number) {
  await sendPacket({ type: "SetTextToSpeechPitch", pitch });
}

export async function setNativeApplicationTextToSpeechRate(rate: number) {
  await sendPacket({ type: "SetTextToSpeechRate", rate });
}

export async function setNativeApplicationTextToSpeechVolume(volume: number) {
  await sendPacket({ type: "SetTextToSpeechVolume", volume });
}

export function isNativeApplication() {
  return Boolean(window.Android) || Boolean(window.webkit?.messageHandlers?.iOS);
}

export function getNativeApplicationEdition() {
  if (window.Android) {
    return "Android";
  } else if (window.webkit?.messageHandlers?.iOS) {
    return "Ios";
  } else {
    throw new Error("ネイティブアプリケーションではありません。");
  }
}

export function useNativeApplicationIdentifierQuery(options?: {
  refetchInterval?: number | false;
  staleTime?: number;
}) {
  return useQuery({
    queryKey: QueryKey("NativeApplicationIdentifier"),
    async queryFn() {
      const response = await sendPacket({ type: "IdentifierRequest" }, true);
      switch (response.type) {
        case "IdentifierResponse":
          return response.identifier;
        case "Error":
          if (response.code === "NOT_IMPLEMENTED") {
            throw new Error(t({ ja: "インストール識別子の取得に対応していません。" }));
          } else {
            console.error("インストール識別子の取得に失敗しました。", response);
            throw new Error(t({ ja: "インストール識別子の取得に失敗しました。" }));
          }
        default:
          console.error("インストール識別子の取得に失敗しました。", response);
          throw new Error(t({ ja: "インストール識別子が予期しない形式です。" }));
      }
    },

    enabled: isNativeApplication(),
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: options?.staleTime ?? 60 * 1000,
    ...options,
  });
}

export function useNativeApplicationRevisionQuery(options?: {
  refetchInterval?: number | false;
  staleTime?: number;
}) {
  return useQuery({
    queryKey: QueryKey("NativeApplicationRevision"),
    async queryFn() {
      // TODO: 新しいネイティブアプリケーションが十分に普及したら、このブロックを削除する。
      // 互換性を保つために最初に古い方法で取得を試みる。
      {
        const response = await sendPacket({ type: "GetNativeApplicationInformationRequest" }, true);
        switch (response.type) {
          case "GetNativeApplicationInformationResponse":
            return response.revision;
          case "Error":
            if (response.code === "NOT_IMPLEMENTED") {
              console.debug(
                "新しいネイティブアプリケーションであるため、新しい方法でリビジョンを再取得します。",
              );
            } else {
              console.error("リビジョンの取得に失敗しました。", response);
              throw new Error(t({ ja: "リビジョンの取得に失敗しました。" }));
            }
            break;
          default:
            console.error("リビジョンが予期しない形式です。", response);
            throw new Error(t({ ja: "リビジョンが予期しない形式です。" }));
        }
      }

      const response = await sendPacket({ type: "RevisionRequest" }, true);
      switch (response.type) {
        case "RevisionResponse":
          return response.revision;
        case "Error":
          if (response.code === "NOT_IMPLEMENTED") {
            throw new Error(t({ ja: "リビジョンの取得に対応していません。" }));
          } else {
            console.error("リビジョンの取得に失敗しました。", response);
            throw new Error(t({ ja: "リビジョンの取得に失敗しました。" }));
          }
        default:
          console.error("リビジョンが予期しない形式です。", response);
          throw new Error(t({ ja: "リビジョンが予期しない形式です。" }));
      }
    },

    enabled: isNativeApplication(),
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: options?.staleTime ?? 60 * 1000,
    ...options,
  });
}

export function useNativeApplicationVersionQuery(options?: {
  refetchInterval?: number | false;
  staleTime?: number;
}) {
  return useQuery({
    queryKey: QueryKey("NativeApplicationVersion"),
    async queryFn() {
      // TODO: 新しいネイティブアプリケーションが十分に普及したら、このブロックを削除する。
      // 互換性を保つために最初に古い方法で取得を試みる。
      {
        const response = await sendPacket({ type: "GetNativeApplicationInformationRequest" }, true);
        switch (response.type) {
          case "GetNativeApplicationInformationResponse":
            return response.version;
          case "Error":
            if (response.code === "NOT_IMPLEMENTED") {
              console.debug(
                "新しいネイティブアプリケーションであるため、新しい方法でバージョンを再取得します。",
              );
            } else {
              console.error("バージョンの取得に失敗しました。", response);
              throw new Error(t({ ja: "バージョンの取得に失敗しました。" }));
            }
            break;
          default:
            console.error("バージョンが予期しない形式です。", response);
            throw new Error(t({ ja: "バージョンが予期しない形式です。" }));
        }
      }

      const response = await sendPacket({ type: "VersionRequest" }, true);
      switch (response.type) {
        case "VersionResponse":
          return response.version;
        case "Error":
          if (response.code === "NOT_IMPLEMENTED") {
            throw new Error(t({ ja: "バージョンの取得に対応していません。" }));
          } else {
            console.error("バージョンの取得に失敗しました。", response);
            throw new Error(t({ ja: "バージョンの取得に失敗しました。" }));
          }
        default:
          console.error("バージョンが予期しない形式です。", response);
          throw new Error(t({ ja: "バージョンが予期しない形式です。" }));
      }
    },

    enabled: isNativeApplication(),
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: options?.staleTime ?? 60 * 1000,
    ...options,
  });
}

/**
 * プッシュ通知が利用可能な端末の場合、プッシュ通知受信機タイプを返す。
 *
 * @returns プッシュ通知受信機タイプ
 */
export function getPushNotificationReceiverType() {
  if (window.Android) {
    return PUSH_NOTIFICATION_RECEIVER_TYPE.FCM;
  } else if (window.webkit?.messageHandlers?.iOS) {
    return PUSH_NOTIFICATION_RECEIVER_TYPE.APNS;
  } else {
    return undefined;
  }
}

function PushNotificationTokenQueryKey() {
  return QueryKey("PushNotificationToken");
}

export function updatePushNotificationTokenCache(
  token?: string | { (token: string | null | undefined): string | null | undefined },
  options?: Partial<{ invalidate: boolean }>,
) {
  const queryKey = PushNotificationTokenQueryKey();
  if (token) {
    TANSTACK_QUERY_CLIENT.setQueryData(queryKey, token);
  }
  if (options?.invalidate) {
    TANSTACK_QUERY_CLIENT.invalidateQueries({ exact: true, queryKey });
  }
}

export function usePushNotificationTokenQuery(options?: {
  refetchInterval?: number | false;
  staleTime?: number;
}) {
  return useQuery({
    queryKey: PushNotificationTokenQueryKey(),
    async queryFn() {
      // TODO: 新しいネイティブアプリケーションが十分に普及したら、このブロックを削除する。
      // 互換性を保つために最初に古い方法で取得を試みる。
      {
        const response = await sendPacket({ type: "GetPushNotificationTokenRequest" }, true);
        switch (response.type) {
          case "GetPushNotificationTokenResponse":
            return response.token;
          case "Error":
            if (response.code === "NOT_IMPLEMENTED") {
              console.debug(
                "新しいネイティブアプリケーションであるため、新しい方法でプッシュ通知トークンを再取得します。",
              );
            } else {
              console.error("プッシュ通知トークンの取得に失敗しました。", response);
              throw new Error(t({ ja: "プッシュ通知トークンの取得に失敗しました。" }));
            }
            break;
          default:
            console.error("プッシュ通知トークンが予期しない形式です。", response);
            throw new Error(t({ ja: "プッシュ通知トークンが予期しない形式です。" }));
        }
      }

      const response = await sendPacket({ type: "PushNotificationTokenRequest" }, true);
      switch (response.type) {
        case "PushNotificationTokenResponse":
          return response.token;
        case "Error":
          if (response.code === "NOT_IMPLEMENTED") {
            throw new Error(t({ ja: "プッシュ通知トークンの取得に対応していません。" }));
          } else {
            console.error("プッシュ通知トークンの取得に失敗しました。", response);
            throw new Error(t({ ja: "プッシュ通知トークンの取得に失敗しました。" }));
          }
        default:
          console.error("プッシュ通知トークンが予期しない形式です。", response);
          throw new Error(t({ ja: "プッシュ通知トークンが予期しない形式です。" }));
      }
    },

    enabled: isNativeApplication(),
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: options?.staleTime ?? 60 * 1000,
    ...options,
  });
}

export const useExternalNavigationRequest = create<{
  data: NativeApplicationPushNotificationData | null;
}>(function () {
  return { data: null };
});

// iOSのWebViewではプログラムから音声合成を行うために、
// 一度ユーザーの操作を引き金にして音声合成を先に一度行っておく必要がある。
if (window.webkit?.messageHandlers?.iOS) {
  function listener() {
    const speechSynthesisUtterance = new SpeechSynthesisUtterance("");
    speechSynthesisUtterance.onend = function () {
      window.removeEventListener("click", listener);
    };
    speechSynthesisUtterance.onerror = function (event) {
      console.error(event.error);
      window.removeEventListener("click", listener);
    };
    speechSynthesisUtterance.lang = "ja-JP";
    window.speechSynthesis.speak(speechSynthesisUtterance);
  }

  window.addEventListener("click", listener);
}

// ネイティブアプリケーションからのメッセージを受け取るための関数をグローバルに公開する。
window.receiveMessageFromNativeApplication = receiveMessageFromNativeApplication;

// アプリケーションが閉じられる時に、
// メッセージが受け取れなくなる事をネイティブアプリケーションに知らせる。
window.addEventListener("beforeunload", function () {
  sendPacket({ type: "Stop" });
});

// メッセージを受け取れるようになった事をネイティブアプリケーションに知らせる。
sendPacket({ type: "Start" });

export const nativeApplication = {
  async open(url: string) {
    await sendPacket({ type: "Open", url });
  },

  async play(file: string, options?: Partial<{ volume: number }>) {
    const response = await sendPacket({ type: "PlayRequest", file, volume: options?.volume }, true);
    if (response.type !== "PlayResponse") {
      throw new Error("`PlayResponse`以外のレスポンスが返されました。" + JSON.stringify(response));
    }
  },

  /**
   * 音声合成を行う。
   * `pitch`・`rate`・`volume`はJavaScriptの`SpeechSynthesisUtterance`を基準に設定すると、
   * 自動的にそれぞれのネイティブアプリケーションに合わせて調整される。
   */
  async speak(text: string, options?: Partial<{ pitch: number; rate: number; volume: number }>) {
    // JavaScriptの`SpeechSynthesisUtterance`を基準に調整する。
    let rate, pitch, volume;
    if (window.Android) {
      rate = options?.rate !== undefined ? Math.min(options.rate, 2.0) : undefined;
      pitch = options?.pitch;
      volume = options?.volume;
    } else if (window.webkit?.messageHandlers?.iOS) {
      rate =
        options?.rate !== undefined ? Math.max(Math.min(options.rate * 0.5, 1.0), 0.1) : undefined;
      pitch = options?.pitch !== undefined ? Math.max(options.pitch, 0.5) : undefined;
      volume = options?.volume;
    }

    // TODO: 新しいネイティブアプリケーションが十分に普及したら、このブロックを削除する。
    // 互換性を保つために最初に古い方法で試行する。
    {
      const response = await sendPacket(
        { type: "TextToSpeechRequest", pitch, rate, text, volume },
        true,
      );
      switch (response.type) {
        case "TextToSpeechResponse":
          return;
        case "Error":
          if (response.code === "NOT_IMPLEMENTED") {
            console.debug(
              "新しいネイティブアプリケーションであるため、新しい方法で音声合成を行います。",
            );
          } else {
            throw new Error(
              "`SpeakResponse`以外のレスポンスが返されました。" + JSON.stringify(response),
            );
          }
          break;
        default:
          throw new Error(
            "`SpeakResponse`以外のレスポンスが返されました。" + JSON.stringify(response),
          );
      }
    }

    const response = await sendPacket({ type: "SpeakRequest", pitch, rate, text, volume }, true);
    switch (response.type) {
      case "SpeakResponse":
        return;
      case "Error":
        switch (response.code) {
          case "NOT_IMPLEMENTED":
            throw new Error("ネイティブアプリケーションの音声合成機能は実装されていません。");
          default:
            throw new Error(
              "`SpeakResponse`以外のレスポンスが返されました。" + JSON.stringify(response),
            );
        }
      default:
        throw new Error(
          "`SpeakResponse`以外のレスポンスが返されました。" + JSON.stringify(response),
        );
    }
  },
};
